/**
 * Admin Help Questions Page
 *
 * The writing backlog behind Ask Sparky: questions it could not answer, was
 * unsure about, or that people marked not helpful. Similar questions are
 * grouped and counted, newest group first. A group can be marked answered once
 * a guide covers it, and "Write a guide" opens GitHub's new-file form with the
 * guide started from the group's words. Rows hold the masked question, never
 * who asked.
 * Access: platform_admin and platform_support.
 */

import { useMemo, useState } from "react";
import {
  CheckCircle,
  PencilSimpleLine,
  Question,
  ArrowsClockwise as RefreshCw,
} from "@phosphor-icons/react";
import {
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  EmptyState,
  HeaderAction,
  PageHeader,
  type PillTone,
  StatusPill,
} from "@charisol-spark/ui";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import {
  AdminListToolbar,
  type ToolbarSortOption,
} from "@/components/lists/AdminListToolbar";
import {
  type ListUrlConfig,
  sortRows,
  useListUrlState,
} from "@/components/lists/useListUrlState";
import {
  useAdminHelpQuestions,
  useSetHelpQuestionStatus,
} from "@/hooks/useAdmin";
import type {
  HelpQuestionGroup,
  HelpQuestionReason,
  HelpQuestionStatus,
} from "@/services/admin";
import { getErrorMessage } from "@/services/api";
import { cn } from "@/utils/cn";
import { writeGuideUrl } from "@/utils/guideTemplate";

const REASONS: Array<{ value: HelpQuestionReason; label: string }> = [
  { value: "no_answer", label: "No answer" },
  { value: "low_confidence", label: "Low confidence" },
  { value: "not_helpful", label: "Not helpful" },
];

const REASON_TONES: Record<HelpQuestionReason, PillTone> = {
  no_answer: "red",
  low_confidence: "amber",
  not_helpful: "orange",
};

const STATUS_TONES: Record<HelpQuestionStatus, PillTone> = {
  open: "blue",
  answered: "green",
};

const ROW_COLUMNS = "minmax(0,1fr) 72px 130px 90px 200px";
const PAGE = 25;

const SORT_OPTIONS: readonly ToolbarSortOption[] = [
  { field: "lastAskedAt", label: "Last asked" },
  { field: "count", label: "Times asked", kind: "number" },
];

/** Newest group first unless the address says otherwise. */
const URL_CONFIG: ListUrlConfig = {
  filterKeys: ["status", "reason", "from", "to"],
  sortFields: SORT_OPTIONS.map((o) => o.field),
  defaultSort: { field: "lastAskedAt", dir: "desc" },
};

const SORT_GETTERS: Record<
  string,
  (group: HelpQuestionGroup) => string | number | undefined
> = {
  lastAskedAt: (g) => g.lastAskedAt,
  count: (g) => g.count,
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function oneOf<T extends string>(
  value: string | undefined,
  allowed: readonly T[]
): T | undefined {
  return allowed.find((a) => a === value);
}

export function AdminHelpQuestionsPage() {
  const list = useListUrlState(URL_CONFIG);
  const status = oneOf(list.filters.status, ["open", "answered"] as const);
  const reason = oneOf(
    list.filters.reason,
    REASONS.map((r) => r.value)
  );
  const filterKey = JSON.stringify([
    list.search,
    status,
    reason,
    list.filters.from,
    list.filters.to,
  ]);
  // "Load more" asks the server for more groups; a new filter starts over.
  const [loaded, setLoaded] = useState({ key: filterKey, limit: PAGE });
  const limit = loaded.key === filterKey ? loaded.limit : PAGE;

  const { data, isLoading, error, refetch, isFetching } = useAdminHelpQuestions({
    status,
    reason,
    search: list.search || undefined,
    from: list.filters.from || undefined,
    to: list.filters.to || undefined,
    limit,
  });
  const setStatus = useSetHelpQuestionStatus();

  const groups = useMemo(
    () => sortRows(data?.groups ?? [], list.sort, SORT_GETTERS),
    [data?.groups, list.sort]
  );
  const today = useMemo(() => new Date(), []);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Help questions"
        description="Questions Ask Sparky could not answer, was unsure about, or that people marked not helpful. This is the backlog for the Help guides: similar questions are grouped, and nothing here says who asked."
        action={
          <HeaderAction
            icon={
              <RefreshCw
                className={cn("h-4 w-4", isFetching && "animate-spin")}
              />
            }
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh
          </HeaderAction>
        }
      />

      <AdminListToolbar
        label="Help questions"
        search={{
          value: list.search,
          onChange: list.setSearch,
          placeholder: "Search the question...",
          scope: "server",
        }}
        filters={[
          {
            id: "status",
            label: "Status",
            kind: "select",
            options: [
              { value: "", label: "All statuses" },
              { value: "open", label: "Open" },
              { value: "answered", label: "Answered" },
            ],
            value: list.filters.status ?? "",
            onChange: (v) => list.setFilter("status", v),
          },
          {
            id: "reason",
            label: "Why it was written down",
            kind: "select",
            options: [{ value: "", label: "Any reason" }, ...REASONS],
            value: list.filters.reason ?? "",
            onChange: (v) => list.setFilter("reason", v),
          },
          {
            id: "from",
            label: "From",
            kind: "date",
            value: list.filters.from ?? "",
            max: list.filters.to || undefined,
            onChange: (v) => list.setFilter("from", v),
          },
          {
            id: "to",
            label: "To",
            kind: "date",
            value: list.filters.to ?? "",
            min: list.filters.from || undefined,
            onChange: (v) => list.setFilter("to", v),
          },
        ]}
        sort={{
          options: SORT_OPTIONS,
          value: list.sort,
          onChange: list.setSort,
          scope: "page",
        }}
        hasActive={list.hasActive}
        onClear={list.clear}
      />

      {setStatus.isError && (
        <Alert variant="error">
          {getErrorMessage(
            setStatus.error,
            "Could not change the status. Try again."
          )}
        </Alert>
      )}

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error ? (
        <Alert variant="error">
          {getErrorMessage(
            error,
            "Could not load help questions. Refresh to retry."
          )}
        </Alert>
      ) : (
        <>
          <DataTable>
            {groups.length === 0 ? (
              <EmptyState
                icon={Question}
                title="No help questions found."
                description={
                  list.hasActive
                    ? "Try different filters, or clear them."
                    : "Ask Sparky has answered everything it was asked so far."
                }
              />
            ) : (
              <>
                <DataTableHead columns={ROW_COLUMNS}>
                  <span>Question</span>
                  <span>Times</span>
                  <span>Last asked</span>
                  <span>Status</span>
                  <span>Actions</span>
                </DataTableHead>
                <DataTableBody>
                  {groups.map((group) => (
                    <DataTableRow
                      key={group.groupKey}
                      columns={ROW_COLUMNS}
                      className="items-start"
                    >
                      <div className="min-w-0 space-y-1.5">
                        <p className="break-words text-sm font-medium">
                          {group.question}
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {REASONS.filter((r) => group.reasons[r.value] > 0).map(
                            (r) => (
                              <StatusPill
                                key={r.value}
                                tone={REASON_TONES[r.value]}
                              >
                                {r.label} {group.reasons[r.value]}
                              </StatusPill>
                            )
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {group.topSlugs.length > 0
                            ? `Closest guides: ${group.topSlugs.join(", ")}`
                            : "No guide came close."}
                          {" · "}
                          {group.plans.join(", ")}
                        </p>
                      </div>
                      <span className="text-sm tabular-nums">
                        {group.count} {group.count === 1 ? "time" : "times"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(group.lastAskedAt)}
                      </span>
                      <div>
                        <StatusPill tone={STATUS_TONES[group.status]}>
                          {group.status === "open" ? "Open" : "Answered"}
                        </StatusPill>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <a
                          href={writeGuideUrl(group, today)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex h-[var(--control-h-sm,2.25rem)] items-center rounded-full border border-input px-3 text-sm font-medium hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <PencilSimpleLine className="mr-1.5 h-4 w-4" />
                          Write a guide
                        </a>
                        <Button
                          variant="outline"
                          size="sm"
                          pill
                          disabled={setStatus.isPending}
                          onClick={() =>
                            setStatus.mutate({
                              ids: group.questionIds,
                              status:
                                group.status === "open" ? "answered" : "open",
                            })
                          }
                          aria-label={`${group.status === "open" ? "Mark answered" : "Reopen"}: ${group.question}`}
                        >
                          <CheckCircle className="mr-1.5 h-4 w-4" />
                          {group.status === "open" ? "Mark answered" : "Reopen"}
                        </Button>
                      </div>
                    </DataTableRow>
                  ))}
                </DataTableBody>
              </>
            )}
          </DataTable>

          {data?.truncated && (
            <p className="text-center text-xs text-muted-foreground">
              Older questions exist than the ones grouped here. Narrow the dates
              to reach them.
            </p>
          )}
          {data?.hasMore && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                pill
                disabled={isFetching}
                onClick={() =>
                  setLoaded({ key: filterKey, limit: limit + PAGE })
                }
              >
                Load more
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
